import { BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";
import { FolderSelection } from "./features/selection";

export type { FolderSelection };

/** File info returned from expanding selections */
export interface ExpandedFileInfo {
  fileId: string;
  fileName: string;
  fileType?: string;
  // The picked folder this file was expanded from, in the picker's encoding
  // (drive id first when the selection named one); absent for a file the
  // caller picked itself (#11207).
  folderPath?: string[];
  // Finpilot's processing of this file failed (#13887): it is returned, not
  // counted as processing, and marked so the composer can say so.
  processingFailed?: boolean;
}

/** Result of expanding selections, including processing info */
export interface ExpandSelectionsResult {
  files: ExpandedFileInfo[];
  processingCount: number;
}

/**
 * Expand folder selections to file metadata by calling the backend API.
 * This is a shared utility used by Matrix, Chat, and Reports.
 *
 * Returns file objects with both ID and name, plus a count of files
 * that were excluded because they are still being processed.
 */
export async function expandSelectionsToFileIds(
  fileIds: string[],
  folderSelections: FolderSelection[],
  excludeBlobKinds?: string[]
): Promise<ExpandSelectionsResult> {
  // If no selections at all, return empty
  if (fileIds.length === 0 && folderSelections.length === 0) {
    return { files: [], processingCount: 0 };
  }

  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/users/files/expandSelections`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fileIds,
        folderSelections,
        ...(excludeBlobKinds &&
          excludeBlobKinds.length > 0 && { excludeBlobKinds }),
      }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to expand folder selections");
  }

  const data = await response.json();
  return {
    files: data.files || [],
    processingCount: data.processingCount || 0,
  };
}
